import React, { useState, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './DocumentEditor.css';

const DocumentEditor = () => {
  const navigate = useNavigate();
  const { isGuest } = useAuth();
  const [title, setTitle] = useState('');
  const [sender, setSender] = useState('');
  const [content, setContent] = useState('');
  const [signature, setSignature] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [loadedHistoryId, setLoadedHistoryId] = useState(null);
  const quillRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // 获取认证token
  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // 检查登录状态
  const checkLogin = () => {
    if (isGuest) {
      navigate('/login');
      return false;
    }
    return true;
  };

  // 加载历史记录
  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    // 检查是否已登录
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.log('未登录，跳过加载历史记录');
      setHistoryList([]);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/document/history`, {
        headers: getAuthHeaders()
      });
      setHistoryList(response.data.data || []);
    } catch (error) {
      console.error('加载历史记录失败:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        navigate('/login');
      }
    }
  };

  const convertHtmlToText = (html) => {
    return html.replace(/<br\s*\/?>/gi, '\n');
  };

  const handleGenerateDocument = async () => {
    // 检查登录状态
    if (!checkLogin()) return;

    if (!title.trim()) {
      alert('请输入公文题目');
      return;
    }

    setLoading(true);
    try {
      const plainContent = convertHtmlToText(content);
      const data = {
        title,
        sender,
        content: plainContent,
        signature,
        date,
      };

      // 第一步：生成文档并获取下载ID
      const genResponse = await axios.post(`${API_BASE_URL}/generate-doc`, data);
      const downloadId = genResponse.data.download_id;

      // 第二步：触发文件下载
      const link = document.createElement('a');
      link.href = `${API_BASE_URL}/download/${downloadId}`;
      link.setAttribute('download', `${title}.docx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 自动保存到历史记录
      await autoSaveDocument();

    } catch (error) {
      console.error('文档生成失败:', error);
      alert(error.response?.data?.detail || '文档生成失败');
    } finally {
      setLoading(false);
    }
  };

  // 自动保存当前公文
  const autoSaveDocument = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.log('未登录，跳过自动保存');
      return;
    }

    try {
      const plainContent = convertHtmlToText(content);
      const documentData = {
        title,
        sender,
        content: plainContent,
        signature,
        date
      };

      const saveTitle = title || `公文排版_${new Date().toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/[\/\s:]/g, '-')}`;

      if (loadedHistoryId) {
        // 更新已加载的历史记录
        await axios.put(`${API_BASE_URL}/api/document/history/${loadedHistoryId}?title=${encodeURIComponent(saveTitle)}`,
          documentData,
          { headers: getAuthHeaders() }
        );
        console.log('自动更新成功');
      } else {
        // 创建新的历史记录
        await axios.post(`${API_BASE_URL}/api/document/save?title=${encodeURIComponent(saveTitle)}`,
          documentData,
          { headers: getAuthHeaders() }
        );
        console.log('自动保存成功');
      }

      await loadHistory();
    } catch (error) {
      console.error('自动保存失败:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        return;
      }
    }
  };

  // 快速保存
  const handleQuickSave = async () => {
    if (!checkLogin()) return;

    if (!title.trim()) {
      alert('请输入公文题目后再保存');
      return;
    }

    setLoading(true);
    try {
      const plainContent = convertHtmlToText(content);
      const documentData = {
        title,
        sender,
        content: plainContent,
        signature,
        date
      };

      const saveTitle = title || `公文排版_${new Date().toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/[\/\s:]/g, '-')}`;

      if (loadedHistoryId) {
        // 更新已加载的历史记录
        await axios.put(`${API_BASE_URL}/api/document/history/${loadedHistoryId}?title=${encodeURIComponent(saveTitle)}`,
          documentData,
          { headers: getAuthHeaders() }
        );
        alert('公文更新成功');
      } else {
        // 创建新的历史记录
        const response = await axios.post(`${API_BASE_URL}/api/document/save?title=${encodeURIComponent(saveTitle)}`,
          documentData,
          { headers: getAuthHeaders() }
        );
        setLoadedHistoryId(response.data.data.id);
        alert('公文保存成功');
      }

      await loadHistory();
    } catch (error) {
      console.error('保存失败:', error);
      if (error.response?.status === 401) {
        alert('登录已过期，请重新登录');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        navigate('/login');
      } else {
        alert(error.response?.data?.detail || '保存失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 加载历史记录
  const handleLoadHistory = async (historyId) => {
    if (!window.confirm('加载历史记录将覆盖当前内容，确定继续？')) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/document/history/${historyId}`, {
        headers: getAuthHeaders()
      });

      const data = response.data.data;
      const docData = data.document_data;

      setTitle(docData.title || '');
      setSender(docData.sender || '');
      setContent(docData.content || '');
      setSignature(docData.signature || '');
      setDate(docData.date || format(new Date(), 'yyyy-MM-dd'));
      setLoadedHistoryId(historyId);

      alert('历史记录加载成功');
    } catch (error) {
      console.error('加载历史记录失败:', error);
      if (error.response?.status === 401) {
        alert('登录已过期，请重新登录');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        navigate('/login');
      } else {
        alert(error.response?.data?.detail || '加载失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 删除历史记录
  const handleDeleteHistory = async (historyId) => {
    if (!window.confirm('确定要删除这条历史记录吗？')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/document/history/${historyId}`, {
        headers: getAuthHeaders()
      });

      // 如果删除的是当前加载的记录，清空loadedHistoryId
      if (loadedHistoryId === historyId) {
        setLoadedHistoryId(null);
      }

      alert('历史记录删除成功');
      await loadHistory();
    } catch (error) {
      console.error('删除历史记录失败:', error);
      if (error.response?.status === 401) {
        alert('登录已过期，请重新登录');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        navigate('/login');
      } else {
        alert(error.response?.data?.detail || '删除失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 新建公文
  const handleNewDocument = () => {
    if (!window.confirm('新建公文将清空当前内容，确定继续？')) {
      return;
    }
    setTitle('');
    setSender('');
    setContent('');
    setSignature('');
    setDate(format(new Date(), 'yyyy-MM-dd'));
    setLoadedHistoryId(null);
  };

  const handleDateChange = (e) => {
    const parsedDate = parseISO(e.target.value);
    setDate(parsedDate ? format(parsedDate, 'yyyy-MM-dd') : e.target.value);
  };

  return (
    <div className="document-editor-page">
      <div className="document-editor-container">
        {/* 左侧历史记录面板 */}
        <div className="history-sidebar">
          <div className="history-header">
            <h3>历史记录</h3>
          </div>
          <div className="history-list">
            {historyList.length === 0 ? (
              <div className="empty-history">暂无历史记录</div>
            ) : (
              historyList.map((item) => (
                <div
                  key={item.id}
                  className={`history-item ${loadedHistoryId === item.id ? 'active' : ''}`}
                >
                  <div className="history-title-row">
                    <div className="history-title">{item.title}</div>
                    <div className="history-actions">
                      <button
                        className="load-btn"
                        onClick={() => handleLoadHistory(item.id)}
                        disabled={loading}
                        title="加载"
                      >
                        加载
                      </button>
                      <button
                        className="delete-history-btn"
                        onClick={() => handleDeleteHistory(item.id)}
                        disabled={loading}
                        title="删除"
                      >
                        删除
                      </button>
                    </div>
                  </div>
                  <div className="history-time">{item.updated_at || item.created_at}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 右侧编辑区域 */}
        <div className="editor-main">
          {/* 滚动提示条 */}
          <div className="marquee-container">
            <div className="marquee-content">
              提示：本网站仅作为公文标准格式的初稿排版，请勿直接将个人或单位（企业）内部或涉密内容直接输入！
            </div>
          </div>

          {/* 主题输入 */}
          <div className="form-group">
            <input
              placeholder="请输入公文题目..."
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="title-input"
            />
          </div>

          {/* 发送方 */}
          <div className="form-group">
            <input
              placeholder="发送方（如：XX单位）"
              type="text"
              value={sender}
              onChange={(e) => setSender(e.target.value)}
            />
          </div>

          {/* 富文本编辑器 */}
          <div className="form-group editor-wrapper">
            <ReactQuill
              ref={quillRef}
              value={content}
              onChange={setContent}
              placeholder="请输入公文内容： 一、二、三、 以此样式开头，识别为标题1。
              （一）（二）（三）以此样式开头，识别为标题2 。
              1. 2. 3. 以此样式开头，识别为标题3"
            />
          </div>

          {/* 落款 */}
          <div className="form-group">
            <input
              placeholder="落款（如：XX单位）"
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
            />
          </div>

          {/* 日期 */}
          <div className="form-group">
            <input
              type="date"
              value={date}
              onChange={handleDateChange}
            />
          </div>

          {/* 操作按钮 */}
          <div className="editor-actions">
            <button
              className="action-btn secondary"
              onClick={handleNewDocument}
              disabled={!isGuest && loading}
            >
              新建
            </button>
            <button
              className="action-btn save-action"
              onClick={handleQuickSave}
              disabled={!isGuest && (loading || !title.trim())}
            >
              {loading ? '保存中...' : '保存'}
            </button>
            <button
              className="action-btn primary"
              onClick={handleGenerateDocument}
              disabled={!isGuest && (loading || !title.trim())}
            >
              {loading ? '生成中...' : '生成公文'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentEditor;
